/**
 * Notas del cliente: las de Chatwoot, en vivo.  (spec 037)
 *
 * FR-006: un tope por ventana, las nuevas arriba, «ver más» y
 * scroll interno. Sin eso, Al equipo se lo come la libreta.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSoloLectura } from '../auth'
import { api, ErrorApi } from '../lib/api'
import { cuerpoNota, esIncidencia, textoIncidencia, VENTANA_NOTAS, VENTANA_NOTAS_COMPACTA, ventanaDeNotas } from '../lib/notas'
import { fechaHora } from '../lib/tiempo'
import { Button } from './ui/button'

interface Nota {
    id: number
    contenido: string
    at: string | null
    autor: string | null
}

interface Lista {
    ok: boolean
    motivo: string | null
    notas: Nota[]
}

export function NotasCliente({
    accountId,
    contactId,
    compacto = false,
}: {
    accountId: string
    contactId: number
    compacto?: boolean
}) {
    const qc = useQueryClient()
    const clave = ['notas-contacto', accountId, contactId] as const
    const paso = compacto ? VENTANA_NOTAS_COMPACTA : VENTANA_NOTAS
    const [limite, setLimite] = useState(paso)
    const q = useQuery<Lista>({
        queryKey: clave,
        queryFn: () => api.get<Lista>(`/business/${accountId}/clientes/${contactId}/notas`),
    })
    const [texto, setTexto] = useState('')
    const envio = useMutation({
        mutationFn: (cuerpo: string) =>
            api.post<{ ok: true }>(`/business/${accountId}/clientes/${contactId}/notas`, { texto: cuerpo }),
        onSuccess: async () => {
            setTexto('')
            setLimite(paso)
            await qc.invalidateQueries({ queryKey: clave })
        },
    })
    const guardar = (incidencia: boolean) => {
        const cuerpo = incidencia ? textoIncidencia(texto) : texto.trim()
        if (cuerpo.trim() === '' || envio.isPending) return
        envio.mutate(cuerpo)
    }

    const soloLectura = useSoloLectura()
    const notas = q.data?.ok ? q.data.notas : []
    const { visibles, restantes } = ventanaDeNotas(notas, limite)
    const errorEnvio =
        envio.error instanceof ErrorApi ? envio.error.message : envio.isError ? 'No se pudo guardar.' : null

    return (
        <section data-testid="notas-cliente" className="space-y-2">
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Notas</h4>
            {q.isPending && <p className="text-xs text-neutral-500">Cargando notas…</p>}
            {q.data && !q.data.ok && (
                <p className="text-xs text-amber-300">
                    No se pudieron leer{q.data.motivo ? `: ${q.data.motivo}` : '.'}
                </p>
            )}
            {notas.length === 0 && q.data?.ok && (
                <p className="text-xs text-neutral-600">Ninguna nota todavía.</p>
            )}
            {visibles.length > 0 && (
                <ul
                    className={
                        compacto
                            ? 'max-h-40 space-y-2 overflow-y-auto'
                            : 'max-h-72 space-y-2 overflow-y-auto'
                    }
                >
                    {visibles.map((n) => (
                        <li
                            key={n.id}
                            className="rounded-md border border-amber-900/40 bg-amber-950/20 px-2.5 py-2"
                        >
                            {esIncidencia(n.contenido) && (
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-rose-300">
                                    Incidencia
                                </p>
                            )}
                            <p className="whitespace-pre-wrap break-words text-xs text-amber-50/90">
                                {cuerpoNota(n.contenido)}
                            </p>
                            <p className="mt-1 text-[10px] text-amber-700/80">
                                {n.autor ?? 'Equipo'}
                                {n.at ? ` · ${fechaHora(n.at)}` : ''}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
            {restantes > 0 && (
                <button
                    type="button"
                    data-testid="notas-ver-mas"
                    className="text-xs text-neutral-400 hover:text-neutral-200"
                    onClick={() => setLimite((n) => n + paso)}
                >
                    Ver más · {restantes} {restantes === 1 ? 'anterior' : 'anteriores'}
                </button>
            )}
            {!soloLectura && (
            <form
                className="space-y-1.5"
                onSubmit={(e) => {
                    e.preventDefault()
                    guardar(false)
                }}
            >
                <label className="block">
                    <span className="sr-only">Nueva nota</span>
                    <textarea
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        rows={compacto ? 2 : 3}
                        maxLength={4000}
                        placeholder="Añadir una nota. Queda en Chatwoot, no la ve el cliente."
                        className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-xs text-neutral-100 placeholder:text-neutral-600 focus:border-amber-700 focus:outline-none"
                    />
                </label>
                {errorEnvio && <p className="text-xs text-rose-400">{errorEnvio}</p>}
                <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={texto.trim() === '' || envio.isPending}>
                        {envio.isPending ? 'Guardando…' : 'Guardar nota'}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={texto.trim() === '' || envio.isPending}
                        onClick={() => guardar(true)}
                    >
                        Incidencia
                    </Button>
                </div>
            </form>
            )}
        </section>
    )
}
